import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "destructive";
  /** Segundos de delay antes de habilitar el botón de confirmar (evita clic reflejo). */
  delaySeconds?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal de confirmación con delay configurable. Por defecto, las acciones
 * destructivas tienen 2s de espera antes de habilitar el botón "Confirmar".
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  variant = "default",
  delaySeconds = variant === "destructive" ? 2 : 0,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [remaining, setRemaining] = useState(delaySeconds);

  useEffect(() => {
    if (!open) return;
    setRemaining(delaySeconds);
    if (delaySeconds === 0) return;
    const interval = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [open, delaySeconds]);

  if (!open) return null;

  const confirmDisabled = remaining > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div className="bg-bg-card rounded-t-md sm:rounded-md shadow-lg w-full sm:max-w-md p-6 space-y-4 animate-slide-up">
        <header>
          <h2 id="confirm-title" className="text-lg font-semibold">
            {title}
          </h2>
          {description && <p className="text-fg-secondary mt-1">{description}</p>}
        </header>
        <footer className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <button onClick={onCancel} className="btn-secondary">
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={confirmDisabled}
            className={cn(
              "btn-primary",
              variant === "destructive" && "bg-error hover:bg-error/90",
            )}
          >
            {confirmDisabled ? `${confirmLabel} (${remaining})` : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
